import { createContext } from "react"

import type { Document } from "@/api/models"
import type { CitationTextAnchor } from "@/utils/citation-anchor-utils"

export type CitationPreviewState = {
  citation: string
  source: string
  page: number | null
  quote: string | null
  textAnchor?: CitationTextAnchor | null
  /** URL loaded in the viewer (indexed or unmodified preview per library flag) */
  documentUrl: string
  /**
   * Original artifact to render instead of `documentUrl`, set only when a
   * faithful viewer exists for its type. The viewer falls back to
   * `documentUrl` when the backend cannot serve the original.
   */
  originalDocumentUrl: string | null
  /** Download target: unmodified blob when present, else indexed */
  downloadUrl: string
  /** Normalized quote for highlighting; primary snippet to try first */
  quoteSnippet: string
  /** Normalized chunk text for fallback when quote fails */
  chunkSnippet: string
  chunkText: string | null
  /** null = no banner; extracted means the preview is derived text, while the original may be separately downloadable. */
  showModifiedPreviewBanner: "converted" | "approximate" | "extracted" | null
  /**
   * The citation names a FILTH-backed library but no delivered document answers
   * to it, so there is nothing to open. `documentUrl` and `downloadUrl` are
   * deliberately empty: the pane names the state rather than requesting a
   * locator no one issued.
   */
  isUnresolvedSource: boolean
}

export type CitationPreviewContextValue = {
  currentCitation: CitationPreviewState | null
  isPreviewOpen: boolean
  openPreview: (
    citation: string,
    citationSection: string,
    options?: {
      chunkText?: string
      documents?: [string, Document][]
      backLabel?: string
      preserveSidebarStack?: boolean
    }
  ) => void
  closePreview: () => void
}

export const CitationPreviewContext = createContext<CitationPreviewContextValue | null>(null)
